import React, { useMemo, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Chip, CircularProgress, Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ReservationsRoutes } from '../../../Router/path';
import { mapBankDocumentDtoToModel } from '../Mapping/bankDocumentsMapper';
import { useGetBankDocumentByIdQuery } from '../Service/bankDocumentsService';
import { getBankDocumentsErrorMessage } from '../Utils/bankDocumentsErrorMapping';
import BankDocumentAllocationDetails from './BankDocumentAllocationDetails';

interface QueueAllocationPreviewProps {
  bankDocumentId: number;
  entryId: number;
  allocationCount: number;
}

const renderReservationLink = (reservationId: number, label: string): React.ReactNode => (
  <Link
    component={RouterLink}
    to={ReservationsRoutes.update.replace(':reservationId', reservationId.toString())}
    underline="hover"
    sx={{ wordBreak: 'break-word' }}
  >
    {label}
  </Link>
);

/** Expandable list of reservation allocations of a queue entry; the document is fetched only after expanding. */
const QueueAllocationPreview: React.FC<QueueAllocationPreviewProps> = ({
  bankDocumentId,
  entryId,
  allocationCount,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { data, isError, error } = useGetBankDocumentByIdQuery(bankDocumentId, {
    skip: !expanded,
  });

  const entry = useMemo(
    () => (data ? mapBankDocumentDtoToModel(data).entries.find((item) => item.id === entryId) : null),
    [data, entryId]
  );
  const panelId = `queue-allocation-preview-${entryId}`;

  const renderContent = (): React.ReactNode => {
    if (isError) {
      return (
        <Alert severity="error">
          {getBankDocumentsErrorMessage(error, 'Nie udało się pobrać rezerwacji pozycji.')}
        </Alert>
      );
    }

    if (!data) {
      return <CircularProgress size={18} aria-label="Ładowanie rezerwacji" />;
    }

    if (!entry) {
      return <Alert severity="warning">Nie znaleziono pozycji w dokumencie bankowym.</Alert>;
    }

    return (
      <BankDocumentAllocationDetails
        entry={{
          ...entry,
          allocations: entry.allocations.filter(
            (allocation) => allocation.targetType === 'reservation'
          ),
        }}
        renderReservationLink={renderReservationLink}
        singleColumn
      />
    );
  };

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Chip
        size="small"
        variant="outlined"
        clickable
        onClick={() => setExpanded((previous) => !previous)}
        aria-expanded={expanded}
        aria-controls={panelId}
        icon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        label={`Pokaż rezerwacje (${allocationCount})`}
      />
      {expanded && (
        <div id={panelId} style={{ width: '100%' }}>
          {renderContent()}
        </div>
      )}
    </Stack>
  );
};

export default QueueAllocationPreview;
